import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */

export const _frontmatter = {
  "pageTitle": "Paragraph API",
  "title": "Paragraph",
  "props": ["ParagraphProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const DocsHeading = makeShortcode("DocsHeading");
const Link = makeShortcode("Link");
const ParagraphProps = makeShortcode("ParagraphProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
<DocsHeading type="api" mdxType="DocsHeading">Paragraph</DocsHeading>
<p>{`The `}<inlineCode parentName="p">{`Paragraph`}</inlineCode>{` component is used to present paragraph content in a clear and consistent manner, by limiting the number to sizes and styles with a UI.`}</p>
<h2>{`Basic Usage`}</h2>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { Paragraph } from 'react-magma-dom';

export function Example() {
  return <Paragraph>Basic paragraph component.</Paragraph>;
}
`}</code></pre>
<h2>{`Sizes`}</h2>
<p>{`There are four visual styles for the paragraph: `}<inlineCode parentName="p">{`bodyLarge`}</inlineCode>{`, `}<inlineCode parentName="p">{`bodyMedium`}</inlineCode>{`, `}<inlineCode parentName="p">{`bodySmall`}</inlineCode>{`, and `}<inlineCode parentName="p">{`bodyXSmall`}</inlineCode>{`, which determine the paragraph's relative size (including font-size, line-height, margins, font-weight, etc).
The default `}<inlineCode parentName="p">{`visualStyle`}</inlineCode>{` is `}<inlineCode parentName="p">{`bodyMedium`}</inlineCode>{`.`}</p>
<p>{`In addition to the paragraph visual styles, the `}<inlineCode parentName="p">{`visualStyle`}</inlineCode>{` prop can also accept the values for the different heading visual styles: `}<inlineCode parentName="p">{`headingXLarge`}</inlineCode>{`, `}<inlineCode parentName="p">{`headingLarge`}</inlineCode>{`, `}<inlineCode parentName="p">{`headingMedium`}</inlineCode>{`, `}<inlineCode parentName="p">{`headingSmall`}</inlineCode>{`, `}<inlineCode parentName="p">{`headingXSmall`}</inlineCode>{` and `}<inlineCode parentName="p">{`heading2XSmall`}</inlineCode>{`.
These visual styles are also used with the `}<Link to="/api/heading" mdxType="Link">{`Heading`}</Link>{` component. The underlying HTML will still render as a `}<inlineCode parentName="p">{`p`}</inlineCode>{` element.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { Paragraph, TypographyVisualStyle } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Paragraph visualStyle={TypographyVisualStyle.headingXLarge}>
        Heading x-large styles.
      </Paragraph>

      <Paragraph visualStyle={TypographyVisualStyle.headingLarge}>
        Heading large styles.
      </Paragraph>

      <Paragraph visualStyle={TypographyVisualStyle.headingMedium}>
        Heading medium styles.
      </Paragraph>

      <Paragraph visualStyle={TypographyVisualStyle.headingSmall}>
        Heading small styles.
      </Paragraph>

      <Paragraph visualStyle={TypographyVisualStyle.headingXSmall}>
        Heading x-small styles.
      </Paragraph>

      <Paragraph visualStyle={TypographyVisualStyle.heading2XSmall}>
        Heading 2x-small styles.
      </Paragraph>

      <Paragraph visualStyle={TypographyVisualStyle.bodyLarge}>
        Body large styles. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph visualStyle={TypographyVisualStyle.bodyMedium}>
        Body medium (default) styles. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph visualStyle={TypographyVisualStyle.bodySmall}>
        Body small styles. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph visualStyle={TypographyVisualStyle.bodyXSmall}>
        Body x-small styles. Lorem ipsum dolar sit amet.
      </Paragraph>
    </>
  );
}
`}</code></pre>
<h2>{`Context Variant`}</h2>
<p>{`The `}<inlineCode parentName="p">{`contextVariant`}</inlineCode>{` prop can be used to set additional styles on a component. The default `}<inlineCode parentName="p">{`contextVariant`}</inlineCode>{` is `}<inlineCode parentName="p">{`default`}</inlineCode>{`.`}</p>
<p>{`The `}<inlineCode parentName="p">{`expressive`}</inlineCode>{` context variant can be used in marketing contexts, which may require more variety than the default styles.`}</p>
<p>{`The `}<inlineCode parentName="p">{`narrative`}</inlineCode>{` context variant can be used for content-heavy contexts, such as a blog-post or an e-reader.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import {
  Paragraph,
  TypographyContextVariant,
  TypographyVisualStyle,
} from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Paragraph
        contextVariant={TypographyContextVariant.expressive}
        visualStyle={TypographyVisualStyle.bodyLarge}
      >
        Body large. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph
        contextVariant={TypographyContextVariant.expressive}
        visualStyle={TypographyVisualStyle.bodyMedium}
      >
        Body medium (default). Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph
        contextVariant={TypographyContextVariant.expressive}
        visualStyle={TypographyVisualStyle.bodySmall}
      >
        Body small. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph
        contextVariant={TypographyContextVariant.expressive}
        visualStyle={TypographyVisualStyle.bodyXSmall}
      >
        Body x-small. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph
        contextVariant={TypographyContextVariant.narrative}
        visualStyle={TypographyVisualStyle.bodyLarge}
      >
        Body large. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph
        contextVariant={TypographyContextVariant.narrative}
        visualStyle={TypographyVisualStyle.bodyMedium}
      >
        Body medium (default). Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph
        contextVariant={TypographyContextVariant.narrative}
        visualStyle={TypographyVisualStyle.bodySmall}
      >
        Body small. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph
        contextVariant={TypographyContextVariant.narrative}
        visualStyle={TypographyVisualStyle.bodyXSmall}
      >
        Body x-small. Lorem ipsum dolar sit amet.
      </Paragraph>
    </>
  );
}
`}</code></pre>
<h2>{`Color`}</h2>
<p>{`The `}<inlineCode parentName="p">{`color`}</inlineCode>{` prop can be used to change the color of the paragraph to convey additional meaning. It accepts the values: `}<inlineCode parentName="p">{`success`}</inlineCode>{`, `}<inlineCode parentName="p">{`danger`}</inlineCode>{` and `}<inlineCode parentName="p">{`subdued`}</inlineCode>{`.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { Paragraph, TypographyColor } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Paragraph color={TypographyColor.success}>
        Success paragraph. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph color={TypographyColor.subdued}>
        Subdued paragraph. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph color={TypographyColor.danger}>
        Danger paragraph. Lorem ipsum dolar sit amet.
      </Paragraph>
    </>
  );
}
`}</code></pre>
<h2>{`Inverse`}</h2>
<p>{`The `}<inlineCode parentName="p">{`isInverse`}</inlineCode>{` property is an optional boolean that changes the colors so the component can appear on a dark background.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { Card, CardBody, Paragraph, TypographyColor } from 'react-magma-dom';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <Paragraph isInverse>
          Inverse paragraph. Lorem ipsum dolar sit amet.
        </Paragraph>

        <Paragraph color={TypographyColor.success} isInverse>
          Success inverse paragraph. Lorem ipsum dolar sit amet.
        </Paragraph>

        <Paragraph color={TypographyColor.subdued} isInverse>
          Subdued inverse paragraph. Lorem ipsum dolar sit amet.
        </Paragraph>

        <Paragraph color={TypographyColor.danger} isInverse>
          Danger inverse paragraph. Lorem ipsum dolar sit amet.
        </Paragraph>
      </CardBody>
    </Card>
  );
}
`}</code></pre>
<h2>{`No Margins`}</h2>
<p>{`By default, the `}<inlineCode parentName="p">{`Paragraph`}</inlineCode>{` component includes margins on the top and bottom. When the `}<inlineCode parentName="p">{`noMargins`}</inlineCode>{` prop is applied, the element will have a margin value of 0.`}</p>
<pre><code parentName="pre" {...{
        "className": "language-tsx"
      }}>{`import React from 'react';
import { Paragraph, TypographyVisualStyle } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Paragraph noMargins visualStyle={TypographyVisualStyle.bodyLarge}>
        Body large. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph noMargins visualStyle={TypographyVisualStyle.bodyMedium}>
        Body medium (default). Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph noMargins visualStyle={TypographyVisualStyle.bodySmall}>
        Body small. Lorem ipsum dolar sit amet.
      </Paragraph>

      <Paragraph noMargins visualStyle={TypographyVisualStyle.bodyXSmall}>
        Body x-small. Lorem ipsum dolar sit amet.
      </Paragraph>
    </>
  );
}
`}</code></pre>
<h2>{`Paragraph Props`}</h2>
<p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the outer `}<inlineCode parentName="strong">{`p`}</inlineCode>{` element.`}</strong></p>
<p>{`All of the `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes"
      }}>{`global HTML attributes`}</a>{` can be provided as props and will be applied to the wrapping `}<inlineCode parentName="p">{`p`}</inlineCode>{` element that gets rendered.`}</p>
<ParagraphProps mdxType="ParagraphProps" />

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      